/**
 * Die Ablage im Reiter Dokumente: der Eintritt in den Massenupload. Sie umschließt den Inhalt des Abschnitts „Positionsdokumente“
 * und nimmt Dateien und Ordner entgegen, die auf die Fläche gezogen werden, oder die der Knopf „Ordner hochladen“ wählt. Mehr tut
 * sie nicht: sie baut den `MassenuploadAuftrag` und gibt ihn an `onStarten`; den Dialog hängt `App.tsx` ein
 * (`MassenuploadDialogLazy`).
 *
 * Dieses Modul gehört zum Eintrittsbündel (siehe `auftrag.ts`): es importiert aus `lib/massenupload/` nichts, auch keinen Typ aus
 * dem Dialog.
 *
 * ================= Wann es eine Ablage gibt =================
 *
 * Nur wenn `bereit` (die Positionsdokumente sind geladen, Fehleranzeige Paket 2, Task 13) UND `massenuploadMoeglich` (Recht
 * `manage_documents`, UUID, eine Position mit Kennung). Sonst gibt es keinen Knopf, keine Eingabe, keinen Zuhörer und kein
 * `data-massenupload-ablage`. Den Rechtedurchgang des Drawers braucht die Ablage deshalb nicht: ohne das Recht gibt es nichts
 * zu sperren.
 *
 * ================= Der Baum bleibt stehen =================
 *
 * Die Komponente wechselt NIE ihre Gestalt: dasselbe Hüllelement mit denselben Kindern an derselben Stelle, ob die Ablage gilt oder
 * nicht (ohne Recht, ohne UUID, nicht bereit). Würde sie zwischen Hülle und bloßen Kindern wechseln, hinge React die Kinder - den
 * Abschnitt "Positionsdokumente" samt seinem Zustand und seinem Vorschlags-Abruf - bei jedem Wechsel des Ladezustands neu ein, und ein
 * Ausfall des Abrufs stünde doppelt im Stapel (Review Task 13, W1, Ruling R-T13c). Ohne Ablage ist die Hülle `display: contents`
 * (`massenupload-ablage-aus`): sie bildet keinen Kasten und ändert nichts am Layout.
 *
 * ================= Ziehen und Ablegen =================
 *
 * Nur Ereignisse mit Dateien (`dataTransfer.types` enthält `"Files"`) werden behandelt; ein gezogener Text bleibt dem Browser.
 * Über einem Positionsblock (`[data-massenupload-position]`, gesetzt in `Positionsdokumente.tsx`) wird die Kennung der Position
 * zur festen Position des Auftrags, sonst verteilt der Hub die Dateien selbst.
 *
 * Der `drop` liest `DataTransfer` SYNCHRON (`ablagequelleAusDrop`): danach sind seine Items leer.
 *
 * ================= Wann sie ein Ziehen NICHT annimmt =================
 *
 * Dann ruft sie trotzdem `preventDefault` und setzt `dropEffect "none"` (sonst öffnete der Browser die Datei und der Hub wäre weg),
 * zeigt aber keine Hervorhebung und startet nichts (`darfAnnehmen`):
 * - das Ziehen begann in der Seite (ein `dragstart` im Dokument, bis `dragend` bzw. `drop`): Chrome meldet für ein gezogenes
 *   `<img>` aus der Seite `"Files"`, das Vorschaubild eines Positionsblocks ginge sonst als Upload auf einen anderen;
 * - das Ziel liegt nicht im DOM der Fläche, das Ereignis kam durch ein React-Portal (die Historie in `Positionsdokumente` hängt ihr
 *   Overlay an den `body`);
 * - im Dokument ist ein anderes Modal offen (`aria-modal="true"`): zwei Fokusfallen auf `document` rufen sich sonst bis zum
 *   Stapelüberlauf gegenseitig, und Tasten landen im verdeckten Feld.
 * Dateien, die im Reiter NEBEN die Ablage fallen, fängt `DokumenteTab` ab (`dateiZiehenNebenDemZielAbfangen`).
 *
 * ================= Ordner hochladen =================
 *
 * Der Knopf klickt die versteckte Eingabe (`webkitdirectory`, `multiple`). Ihre Dateiliste ist lebendig: `value = ""` leert sie.
 * Die Ablage kopiert sie deshalb vorher und setzt `value` zurück, damit dieselbe Wahl noch einmal möglich ist.
 *
 * ================= Fokus =================
 *
 * Die Ablage trägt `tabIndex -1`. Sie ist das Ziel von `fokusZurAblage`, wenn der Dialog beim Schließen keinen Auslöser mehr
 * vorfindet (der Drawer wurde neu aufgebaut): `App.tsx` ruft es nach dem Schließen auf.
 */

import { useEffect, useRef, useState } from "react";
import type { ChangeEvent, DragEvent, JSX, ReactNode } from "react";
import { positionskennung } from "../../../../lib/positionsAnzeige.ts";
import type { Order, OrderItem } from "../../../../lib/types.ts";
import { Ikone } from "../../../Ikone.tsx";
import { ablagequelleAusDrop, massenuploadMoeglich, type MassenuploadAuftrag } from "./auftrag.ts";

export interface MassenuploadAblageProps {
  bestellung: Order;
  rechte: ReadonlySet<string>;
  onStarten: (auftrag: MassenuploadAuftrag) => void;
  /**
   * Ob der Einstieg jetzt bedienbar ist: erst mit geladenen Positionsdokumenten (ein Upload auf unbekanntem Stand legte eine
   * vermeintlich erste Fassung an). Regelt nur, WANN die Ablage gilt; der Auftrag und alles dahinter bleiben unberührt.
   */
  bereit: boolean;
  children: ReactNode;
}

const SELEKTOR_ABLAGE = "[data-massenupload-ablage]";
const SELEKTOR_POSITION = "[data-massenupload-position]";
const SELEKTOR_MODAL = '[aria-modal="true"]';

const HINWEIS_FLAECHE = "Ablegen: Der Hub verteilt die Dateien auf die Positionen";

const enthaeltDateien = (daten: DataTransfer): boolean => Array.from(daten.types).includes("Files");

/**
 * Darf die Ablage dieses Ziehen annehmen? Nicht, wenn
 * - das Ziehen in der Seite begann (`ziehtInSeite`): Chrome meldet beim Ziehen eines `<img>` aus der Seite `"Files"` in
 *   `dataTransfer.types`; das Vorschaubild eines Positionsblocks ginge sonst auf einem anderen als Upload los;
 * - das Ziel nicht im DOM der Fläche liegt: React-Ereignisse steigen entlang des React-Baums auf, auch durch ein Portal. Die
 *   Historie in `Positionsdokumente` hängt ihr Overlay per Portal an den `body`; ein Drop darauf käme sonst hier an;
 * - im Dokument ein anderes Modal offen ist (`aria-modal`: Historie, Ersatz-Dialog, Lightbox ...). Ein Massenupload-Dialog daneben
 *   brächte zwei Fokusfallen gegeneinander auf (je `focusin` auf `document`, bis zum Stapelüberlauf) und schickte Tasten in das
 *   verdeckte Feld.
 */
function darfAnnehmen(ereignis: DragEvent<HTMLElement>, ziehtInSeite: boolean): boolean {
  if (ziehtInSeite) return false;
  if (!(ereignis.target instanceof Node) || !ereignis.currentTarget.contains(ereignis.target)) return false;
  return document.querySelector(SELEKTOR_MODAL) === null;
}

/**
 * Das Ziehen von Dateien ist hier nicht erwünscht, soll aber auch nicht beim Browser landen: `preventDefault` verhindert, dass er
 * die Datei im Tab öffnet und den Hub verlässt, `dropEffect "none"` zeigt dem Nutzer, dass hier nichts abgelegt wird.
 */
function ablehnen(ereignis: DragEvent<HTMLElement>): void {
  ereignis.preventDefault();
  ereignis.dataTransfer.dropEffect = "none";
}

/** Die Kennung des Positionsblocks unter dem Zeiger, oder `null` über der übrigen Fläche. */
function positionUnter(ereignis: DragEvent<HTMLElement>): number | null {
  const ziel = ereignis.target;
  const block = ziel instanceof Element ? ziel.closest<HTMLElement>(SELEKTOR_POSITION) : null;
  const kennung = Number(block?.getAttribute("data-massenupload-position"));
  return Number.isInteger(kennung) && kennung > 0 ? kennung : null;
}

/** „Pos. n“ der Position mit dieser Kennung, gezählt wie im Dialog (`index + 1`); `null`, wenn die Bestellung sie nicht führt. */
function positionsplatz(bestellung: Order, kennung: number): number | null {
  const positionen: unknown = bestellung.items;
  if (!Array.isArray(positionen)) return null;
  const index = (positionen as (OrderItem | null)[]).findIndex(
    (position) => position !== null && positionskennung(position) === kennung,
  );
  return index < 0 ? null : index + 1;
}

/**
 * Der Fokus nach dem Schließen des Dialogs: hat nichts mehr den Fokus (der Dialog hat ihn nicht an einen noch vorhandenen
 * Auslöser zurückgegeben), geht er an die Ablage des Drawers. Ein vorhandener Fokus bleibt. Ohne Ablage (Drawer zu) geschieht nichts.
 */
export function fokusZurAblage(): void {
  const aktiv = document.activeElement;
  if (aktiv !== null && aktiv !== document.body) return;
  document.querySelector<HTMLElement>(SELEKTOR_ABLAGE)?.focus();
}

export function MassenuploadAblage({ bestellung, rechte, onStarten, bereit, children }: MassenuploadAblageProps): JSX.Element {
  return (
    <Ablageflaeche bestellung={bestellung} onStarten={onStarten} aktiv={bereit && massenuploadMoeglich(bestellung, rechte)}>
      {children}
    </Ablageflaeche>
  );
}

interface AblageflaecheProps {
  bestellung: Order;
  onStarten: (auftrag: MassenuploadAuftrag) => void;
  /** Ob die Ablage gilt (bereit und möglich). Sonst bleibt nur die Hülle um die Kinder stehen. */
  aktiv: boolean;
  children: ReactNode;
}

function Ablageflaeche({ bestellung, onStarten, aktiv, children }: AblageflaecheProps): JSX.Element {
  const eingabe = useRef<HTMLInputElement | null>(null);
  // Ein `dragstart` irgendwo im Dokument heißt: dieses Ziehen gehört der Seite, nicht dem Dateisystem. Es endet mit `dragend` oder
  // mit dem `drop`. Der `drop`-Hörer hängt im Bubble-Schritt: React hat das Ereignis am Wurzelelement dann schon behandelt.
  // Beides kann ausbleiben: lehnt die Ablage ein `dragover` ab, feuert im Browser kein `drop`, und `dragend` fehlt manchmal auch.
  // Während eines Ziehens gibt es keine Maus- und Zeigerereignisse; die nächste Bewegung heißt also, dass nichts mehr zieht
  // (Ruling 54). Sonst nähme die Ablage bis zum Neueinhängen nichts mehr an.
  const ziehtInSeite = useRef(false);
  useEffect(() => {
    // Eine Ablage, die nicht gilt, hört auf nichts.
    if (!aktiv) return undefined;
    const beginnt = (): void => {
      ziehtInSeite.current = true;
    };
    const endet = (): void => {
      ziehtInSeite.current = false;
    };
    document.addEventListener("dragstart", beginnt, true);
    document.addEventListener("dragend", endet, true);
    document.addEventListener("drop", endet);
    document.addEventListener("pointermove", endet, { passive: true });
    document.addEventListener("mousemove", endet, { passive: true });
    return () => {
      document.removeEventListener("dragstart", beginnt, true);
      document.removeEventListener("dragend", endet, true);
      document.removeEventListener("drop", endet);
      document.removeEventListener("pointermove", endet);
      document.removeEventListener("mousemove", endet);
    };
  }, [aktiv]);
  // `null`: es wird nichts gezogen. Sonst die Kennung des Blocks unter dem Zeiger, oder `null` über der übrigen Fläche.
  const [ueber, setUeber] = useState<{ position: number | null } | null>(null);
  // Wird die Ablage ungültig, während etwas darüber gezogen wird, bliebe die Hervorhebung für das nächste Mal stehen.
  useEffect(() => {
    if (!aktiv) setUeber(null);
  }, [aktiv]);

  const beimUeberfahren = (ereignis: DragEvent<HTMLElement>): void => {
    if (!enthaeltDateien(ereignis.dataTransfer)) return;
    if (!darfAnnehmen(ereignis, ziehtInSeite.current)) {
      ablehnen(ereignis);
      setUeber(null);
      return;
    }
    // Ohne `preventDefault` kein `drop`, und der Browser öffnet die Datei.
    ereignis.preventDefault();
    ereignis.dataTransfer.dropEffect = "copy";
    const position = positionUnter(ereignis);
    // `dragover` feuert im Takt von Millisekunden: nur bei einer Änderung neu zeichnen.
    setUeber((alt) => (alt !== null && alt.position === position ? alt : { position }));
  };

  const beimVerlassen = (ereignis: DragEvent<HTMLElement>): void => {
    // Der Zeiger wechselt nur auf ein Kind der Fläche: die Hervorhebung bleibt.
    if (ereignis.relatedTarget instanceof Node && ereignis.currentTarget.contains(ereignis.relatedTarget)) return;
    setUeber(null);
  };

  const beimAblegen = (ereignis: DragEvent<HTMLElement>): void => {
    if (!enthaeltDateien(ereignis.dataTransfer)) return;
    if (!darfAnnehmen(ereignis, ziehtInSeite.current)) {
      ablehnen(ereignis);
      setUeber(null);
      return;
    }
    ereignis.preventDefault();
    const festePosition = positionUnter(ereignis);
    // Synchron: nach diesem Ereignis geben die Items des `DataTransfer` nichts mehr her.
    const quelle = ablagequelleAusDrop(ereignis.dataTransfer);
    setUeber(null);
    if (quelle !== null) onStarten({ bestellung, quelle, festePosition });
  };

  const beiOrdnerWahl = (ereignis: ChangeEvent<HTMLInputElement>): void => {
    // Erst kopieren, dann leeren: `value = ""` leert die lebendige Dateiliste.
    const dateien = Array.from(ereignis.currentTarget.files ?? []);
    ereignis.currentTarget.value = "";
    if (dateien.length > 0) onStarten({ bestellung, quelle: { art: "dateien", dateien }, festePosition: null });
  };

  const platz = ueber === null || ueber.position === null ? null : positionsplatz(bestellung, ueber.position);
  const hinweis = ueber === null ? null : platz === null ? HINWEIS_FLAECHE : `Ablegen an Pos. ${String(platz)}`;

  // `webkitdirectory` kennt das Typsystem von React nicht; als Zeichenkette landet es als Attribut im DOM.
  const ordnerAttribut = { webkitdirectory: "" };

  // Dieselbe Hülle in jedem Fall (siehe "Der Baum bleibt stehen"): nur ihre Merkmale und der Kopf hängen an `aktiv`. Der Kopf steht vor
  // den Kindern in einem eigenen Platz, `null` oder Element - die Kinder behalten ihre Stelle.
  const klasse = !aktiv ? "massenupload-ablage-aus" : ueber === null ? "massenupload-ablage" : "massenupload-ablage massenupload-ueber";
  return (
    <div
      className={klasse}
      data-massenupload-ablage={aktiv ? "" : undefined}
      tabIndex={aktiv ? -1 : undefined}
      onDragOver={aktiv ? beimUeberfahren : undefined}
      onDragLeave={aktiv ? beimVerlassen : undefined}
      onDrop={aktiv ? beimAblegen : undefined}
    >
      {aktiv ? (
        <div className="massenupload-kopf">
          <button
            type="button"
            className="chk-btn"
            onClick={() => {
              eingabe.current?.click();
            }}
          >
            <Ikone name="upload" />
            Ordner hochladen
          </button>
          <input
            ref={eingabe}
            type="file"
            multiple
            {...ordnerAttribut}
            aria-label="Ordner für den Massenupload wählen"
            style={{ display: "none" }}
            onChange={beiOrdnerWahl}
          />
          {hinweis === null ? null : (
            <p className="massenupload-hinweis" role="status">
              {hinweis}
            </p>
          )}
        </div>
      ) : null}
      {children}
    </div>
  );
}
